import { Task } from "api/types/users";
import { formatLocalizedDate } from "utils/formatLocalizedDate";
import { BILLING_TYPE_I18N_KEYS } from "./index";

type TFn = (key: string, options?: Record<string, unknown>) => string;

// Mirrors the lookup maps in an-api's
// src/business-logic/user/createTaskUser.ts — each task type's
// distinguishing raw name travels in task.metadata (documentType / formType
// / videoCallType) specifically so it can be re-translated client-side
// instead of trusting task.display_name, which the backend only ever
// generates in English.

const IMAGE_UPLOAD_KEYS: Record<string, string> = {
  INSURANCE_CARD_FRONT: "insuranceCardFront",
  INSURANCE_CARD_BACK: "insuranceCardBack",
  DIAGNOSIS_FORM: "diagnosisForm",
};

const FILLOUT_IFRAME_KEYS: Record<string, string> = {
  ONBOARDING_PACKET: "onboardingPacket",
  CFQL: "cfql",
  SDOH: "sdoh",
  TABLET_REQUEST_FORM: "tabletRequestForm",
  CAREGIVER_SATISFACTION_SURVEY: "caregiverSatisfactionSurvey",
};

const OTHER_CONSENT_FORM_KEYS: Record<string, string> = {
  BEHAVIORAL_HEALTH_SERVICES_CONSENT: "behavioralHealthServicesConsent",
  RESEARCH_CONSENT: "researchConsent",
  TABLET_CONSENT_FORM: "tabletConsentForm",
  PHI_RELEASE: "phiRelease",
  TREATMENT_PLAN_SIGNATURE: "treatmentPlanSignature",
};

/**
 * Translates a task's display name client-side. Falls back to
 * task.display_name, then task.type (English) when a raw name isn't
 * recognized, so new backend task variants degrade gracefully instead of
 * showing a blank label.
 *
 * Note: treatment_plan_signature tasks always resolve to the generic
 * "Treatment Plan Signature" — the backend doesn't preserve which variant
 * (Initial vs Re-Assessment) in metadata the way it does for other task
 * types, so that distinction is lost here too.
 */
export const getTaskDisplayName = (task: Task, t: TFn): string => {
  const metadata = task.metadata || {};
  // Preserve the pre-i18n fallback behavior (task.display_name || task.type)
  // so an empty/missing display_name never renders a blank label.
  const fallback = task.display_name || task.type;

  switch (task.type) {
    case "image_upload": {
      const key = IMAGE_UPLOAD_KEYS[metadata.documentType];
      return key ? t(`dashboard:parent.myTasks.displayNames.imageUpload.${key}`) : fallback;
    }

    case "video_call_signature": {
      const billingKey =
        BILLING_TYPE_I18N_KEYS[metadata.videoCallType as keyof typeof BILLING_TYPE_I18N_KEYS];
      if (!billingKey) return fallback;

      const serviceName = t(`dashboard:parent.nextAppointment.billingTypes.${billingKey}`);
      const scheduledDate = metadata.scheduled_date ? new Date(metadata.scheduled_date) : null;

      if (!scheduledDate) {
        return t("dashboard:parent.myTasks.displayNames.videoCallSignature.templateNoDate", {
          serviceName,
        });
      }

      const date = formatLocalizedDate(scheduledDate, {
        en: "EEEE, MMMM do",
        es: "EEEE, d 'de' MMMM",
      });
      return t("dashboard:parent.myTasks.displayNames.videoCallSignature.template", {
        serviceName,
        date,
      });
    }

    case "treatment_plan_signature":
      return t("dashboard:parent.myTasks.treatmentPlanSignature.defaultTitle");

    case "ninety_day_progress_signature":
      return t("dashboard:parent.myTasks.displayNames.ninetyDayProgressSignature");

    case "fillout_iframe": {
      const key = FILLOUT_IFRAME_KEYS[metadata.formType];
      return key ? t(`dashboard:parent.myTasks.displayNames.filloutIframe.${key}`) : fallback;
    }

    case "other_consent_forms": {
      const key = OTHER_CONSENT_FORM_KEYS[metadata.formType];
      return key
        ? t(`dashboard:parent.myTasks.displayNames.otherConsentForms.${key}`)
        : fallback;
    }

    // No metadata lookup: unlike the mapped types above there is only one
    // tech check task, so the type alone identifies the label.
    case "tech_check":
      return t("dashboard:parent.myTasks.displayNames.techCheck");

    default:
      return fallback;
  }
};
